/** Registro e aplicação controlados: nenhum SW em dev/preview/iframe/?sw=off. */
import { isUpdateContextBlocked, WORKER_INSTALL_WAIT_MS, CONTROLLER_WAIT_MS } from './updatePolicy';
import { scheduleUpdateChecks } from './updateChecks';
import { getAppBusyState, isTabBusy } from './busyRegistry';
import { setPendingUpdate, getCurrentBuild, markUpdateAttempt, recordSwError, getSwError, getRemoteBuild } from './updateState';

const SW_URL = '/sw.js';
let registration: ServiceWorkerRegistration | null = null;
let updateCallback: (() => void) | null = null;
let stopChecks: (() => void) | null = null;
let applying = false;
let lastWorkerCheck = 0;
let workerChecking = false;
let channel: BroadcastChannel | null = null;

export function onServiceWorkerUpdate(cb: () => void) {
  updateCallback = cb;
  return () => { if (updateCallback === cb) updateCallback = null; };
}

export function isBlockedContext(): boolean {
  if (typeof window === 'undefined') return true;
  let framed = true;
  try { framed = window.self !== window.top; } catch { /* bloqueado */ }
  return isUpdateContextBlocked(import.meta.env.PROD, window.location.hostname, framed,
    new URL(window.location.href).searchParams.get('sw') === 'off');
}

function announceUpdate() {
  updateCallback?.();
  window.dispatchEvent(new Event('autofluxo:update-available'));
}

function setupChannel() {
  if (channel || !('BroadcastChannel' in window) || isBlockedContext()) return;
  channel = new BroadcastChannel('autofluxo-update');
  channel.onmessage = (event) => {
    if (event.data?.type !== 'applied' || applying || isBlockedContext()) return;
    // Só a aba local precisa estar livre: uma aba ocupada mantém o aviso.
    setPendingUpdate(String(event.data.version || `sw:${getCurrentBuild()}`));
    if (isTabBusy()) announceUpdate();
    else { markUpdateAttempt(String(event.data.version || `sw:${getCurrentBuild()}`)); window.location.reload(); }
  };
}

async function getRegistration() {
  if (registration) return registration;
  if (!('serviceWorker' in navigator)) return null;
  const regs = await navigator.serviceWorker.getRegistrations();
  registration = regs.find(r => [r.active, r.waiting, r.installing].some(w =>
    w && new URL(w.scriptURL).origin === window.location.origin && new URL(w.scriptURL).pathname === SW_URL)) || null;
  return registration;
}

export async function checkServiceWorkerUpdate() {
  if (isBlockedContext() || workerChecking || (lastWorkerCheck && Date.now() - lastWorkerCheck < 60000)) return;
  lastWorkerCheck = Date.now();
  workerChecking = true;
  try { await (await getRegistration())?.update(); } catch { /* offline */ }
  finally { workerChecking = false; }
}

let workerFailed = false;
const watched = new WeakSet<ServiceWorker>();
export type SwPhase = 'idle' | 'installing' | 'waiting' | 'activating' | 'error';
let phase: SwPhase = 'idle';
function setPhase(p: SwPhase) { phase = p; window.dispatchEvent(new CustomEvent('autofluxo:sw-status', { detail: p })); }
export function getSwPhase() { return phase; }
export function didWorkerFail() { return workerFailed; }

/** Acompanha o SW novo: falha (redundant) fica registrada para diagnóstico e rede de segurança. */
function watchWorker(w: ServiceWorker | null | undefined) {
  if (!w || watched.has(w)) return;
  watched.add(w);
  const sync = () => {
    if (w.state === 'installing') setPhase('installing');
    else if (w.state === 'installed') setPhase('waiting');
    else if (w.state === 'activating') setPhase('activating');
    else if (w.state === 'redundant' && registration?.active !== w) {
      workerFailed = true;
      setPhase('error');
      recordSwError('Instalação do novo service worker falhou (redundant)');
    }
  };
  sync();
  w.addEventListener('statechange', sync);
}
function watchRegistration(reg: ServiceWorkerRegistration | null) {
  if (!reg) return;
  watchWorker(reg.installing);
  if (reg.waiting) { watchWorker(reg.waiting); setPhase('waiting'); }
  reg.addEventListener('updatefound', () => watchWorker(reg.installing));
}

/** Após clique e acquireUpdateWindow livre. Toda recarga revalida busy. */
export async function applyServiceWorkerUpdate(version?: string) {
  if (isBlockedContext()) throw new Error('Contexto bloqueado');
  const safeReload = () => {
    if (getAppBusyState() !== 'free') throw new Error('Trabalho em andamento');
    channel?.postMessage({ type: 'applied', version });
    window.location.reload();
  };
  applying = true;
  try {
    let reg: ServiceWorkerRegistration | null = null;
    try { reg = await getRegistration(); } catch { /* recarga segura sem SW */ }
    if (reg && !reg.waiting) {
      watchRegistration(reg);
      // Espera de verdade pela instalação (precache inteiro no celular) ou falha.
      await new Promise<void>(resolve => {
        const deadline = Date.now() + WORKER_INSTALL_WAIT_MS;
        const timer = window.setInterval(() => {
          const failed = !reg?.installing && !reg?.waiting && phase === 'error';
          if (reg?.waiting || failed || Date.now() >= deadline) { window.clearInterval(timer); resolve(); }
        }, 200);
        void reg?.update().catch((e) => recordSwError(`Falha ao buscar o novo service worker: ${e?.message || e}`));
      });
      if (!reg.waiting && phase !== 'error') recordSwError('Tempo esgotado esperando o novo service worker');
    }
    if (getAppBusyState() !== 'free') throw new Error('Trabalho em andamento');
    if (reg?.waiting) {
      setPhase('activating');
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          window.clearTimeout(timer);
          navigator.serviceWorker.removeEventListener('controllerchange', changed);
        };
        const changed = () => {
          cleanup();
          try { safeReload(); resolve(); } catch (error) { reject(error); }
        };
        const timer = window.setTimeout(() => { recordSwError('Novo service worker não assumiu a tempo'); changed(); }, CONTROLLER_WAIT_MS);
        navigator.serviceWorker.addEventListener('controllerchange', changed);
        try { reg?.waiting?.postMessage({ type: 'SKIP_WAITING' }); }
        catch { changed(); }
      });
    } else safeReload(); // sem SW novo: a navegação NetworkFirst já traz o build novo da rede
  } finally { applying = false; }
}

const APP_CACHE = /^(workbox-precache|autofluxo-html|autofluxo-assets)|-precache-v\d+-/;
/** Limpeza segura: desregistra só o SW do app e apaga só os caches do app; não toca login. */
export async function recoverAppCaches(): Promise<boolean> {
  if (isBlockedContext() || getAppBusyState() !== 'free') return false;
  try {
    await unregisterAppServiceWorkers();
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.allSettled(names.filter(n => APP_CACHE.test(n)).map(n => caches.delete(n)));
    }
  } catch (e) { recordSwError(`Limpeza automática falhou: ${(e as Error)?.message || e}`); }
  if (getAppBusyState() !== 'free') return false;
  window.location.reload();
  return true;
}

async function unregisterAppServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.filter(r => [r.active, r.installing, r.waiting].some(w => {
      if (!w) return false;
      const url = new URL(w.scriptURL);
      return url.origin === window.location.origin && ['/sw.js', '/service-worker.js'].includes(url.pathname);
    })).map(r => r.unregister().catch(() => false)));
  } catch { /* ignore */ }
}

export async function registerAppServiceWorker(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (isBlockedContext()) { stopChecks?.(); await unregisterAppServiceWorkers(); return; }
  setupChannel();
  try {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({
      immediate: true,
      // Workbox chama controlling também em outras abas. A recarga pertence
      // exclusivamente ao fluxo seguro acima, nunca à biblioteca.
      onNeedReload() { if (!applying) announceUpdate(); },
      onNeedRefresh() { setPendingUpdate(`sw:${getCurrentBuild()}`); announceUpdate(); },
      onRegisteredSW(_url, reg) {
        registration = reg || null;
        watchRegistration(registration);
        stopChecks?.();
        stopChecks = scheduleUpdateChecks(checkServiceWorkerUpdate);
      },
    });
  } catch { /* ignore */ }
}

export async function getServiceWorkerStatus() {
  const supported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const reg = supported ? await getRegistration().catch(() => null) : null;
  return { supported, registered: !!reg, waiting: !!reg?.waiting, installing: !!reg?.installing,
    active: !!reg?.active, phase, failed: workerFailed, lastError: getSwError(), remoteBuild: getRemoteBuild(),
    scope: reg?.scope ?? null, scriptUrl: reg?.active?.scriptURL ?? null };
}
